import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SSOLogin } from '@/components/auth/SSOLogin';
import { SSORegister } from '@/components/auth/SSORegister';

export default function Home() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Debug: Check if environment variables are loaded (remove in production)
    if (import.meta.env.DEV) {
      console.log('Environment Variables:', {
        VITE_API_URL: import.meta.env.VITE_API_URL,
        VITE_SSO_APP_URL: import.meta.env.VITE_SSO_APP_URL,
        VITE_SSO_CLIENT_ID: import.meta.env.VITE_SSO_CLIENT_ID ? '***' : undefined,
      });
    }
  }, []);
  
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50/40">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50/30 text-slate-800">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-amber-100/80 bg-white/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-teal-500 text-white shadow-sm">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Phino LMS</h1>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => scrollTo('about')}
                className="hidden sm:inline-flex text-slate-600 hover:text-blue-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollTo('courses')}
                className="hidden sm:inline-flex text-slate-600 hover:text-blue-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Courses
              </button>
              <button
                onClick={() => scrollTo('contact')}
                className="hidden sm:inline-flex text-slate-600 hover:text-blue-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Contact
              </button>
              <button
                onClick={() => scrollTo('login-section')}
                className="ml-1 sm:ml-2 inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-amber-200/40 blur-3xl" />
          <div className="absolute top-20 right-0 h-80 w-80 rounded-full bg-teal-200/30 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-orange-100/50 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center rounded-full bg-amber-100 px-3.5 py-1 text-sm font-medium text-amber-800 ring-1 ring-inset ring-amber-200/80">
                Learning management for students &amp; tutors
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Learn, Teach, and Grow{' '}
                <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                  with Phino LMS
                </span>
              </h1>
              <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                A modern learning management system for courses, enrollments, progress tracking,
                exams, and certificates—built for students and tutors.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <button
                  onClick={() => scrollTo('login-section')}
                  className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-blue-600/25 hover:bg-blue-700 transition-colors"
                >
                  Get Started
                </button>
                <button
                  onClick={() => scrollTo('courses')}
                  className="inline-flex items-center justify-center rounded-xl border border-amber-200 bg-white/80 px-8 py-3.5 text-base font-semibold text-slate-700 hover:bg-amber-50 transition-colors"
                >
                  Explore Courses
                </button>
              </div>
            </div>

            {/* Illustrative panel */}
            <div className="relative mx-auto w-full max-w-md lg:max-w-none">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-amber-200/40 via-teal-100/30 to-blue-100/40 blur-xl" />
              <div className="relative space-y-4">
                <div className="rounded-2xl border border-white/80 bg-white p-5 shadow-xl shadow-amber-900/5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">Full-Stack Web Development</p>
                      <p className="mt-1 text-sm text-slate-500">12 lessons · Tutor-led path</p>
                      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-blue-500 to-teal-400" />
                      </div>
                      <p className="mt-1.5 text-xs font-medium text-teal-600">67% complete</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-white/80 bg-white p-4 shadow-lg shadow-amber-900/5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <p className="mt-3 text-sm font-semibold text-slate-900">Certificate earned</p>
                    <p className="mt-0.5 text-xs text-slate-500">Cloud Fundamentals</p>
                  </div>
                  <div className="rounded-2xl border border-white/80 bg-white p-4 shadow-lg shadow-amber-900/5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-100 text-teal-600">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <p className="mt-3 text-sm font-semibold text-slate-900">Exam ready</p>
                    <p className="mt-0.5 text-xs text-slate-500">Next quiz: Friday</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      <div className="h-8 w-8 rounded-full bg-blue-400 ring-2 ring-white" />
                      <div className="h-8 w-8 rounded-full bg-teal-400 ring-2 ring-white" />
                      <div className="h-8 w-8 rounded-full bg-amber-400 ring-2 ring-white" />
                    </div>
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold text-slate-900">Students &amp; tutors</span> learning together
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-20 bg-gradient-to-b from-amber-50/60 to-orange-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-amber-700">About</h2>
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              What is Phino LMS?
            </p>
            <p className="mt-4 text-lg sm:text-xl text-slate-600 leading-relaxed">
              Phino LMS is PhinoSoft&apos;s learning management platform. Students enroll in courses,
              track progress, take exams, and earn certificates. Tutors create and manage courses,
              assessments, and learner outcomes—all in one place.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {[
              { label: 'Courses', color: 'bg-blue-100 text-blue-800 ring-blue-200' },
              { label: 'Progress', color: 'bg-teal-100 text-teal-800 ring-teal-200' },
              { label: 'Certificates', color: 'bg-amber-100 text-amber-800 ring-amber-200' },
            ].map((chip) => (
              <span
                key={chip.label}
                className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold ring-1 ring-inset ${chip.color}`}
              >
                {chip.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Courses / Learning Paths Section */}
      <section id="courses" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-amber-700">Learning Paths</h2>
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Courses Built for Real Skills
            </p>
            <p className="mt-4 text-lg sm:text-xl text-slate-600">
              Explore structured learning paths in high-demand IT fields, with tutor guidance and measurable progress.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {[
              {
                title: 'Software Development',
                description:
                  'Learn modern development practices, frameworks, and tools through structured courses covering full-stack applications, mobile apps, and web services.',
                items: [
                  'Full-stack web development',
                  'Mobile app development',
                  'API design and development',
                  'Database design and optimization',
                ],
                iconBg: 'bg-blue-100 text-blue-600',
                borderHover: 'hover:border-blue-200',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                ),
              },
              {
                title: 'DevOps Engineering',
                description:
                  'Master continuous integration, deployment, and infrastructure automation through hands-on modules that bridge development and operations.',
                items: [
                  'CI/CD pipeline development',
                  'Infrastructure as Code',
                  'Container orchestration',
                  'Monitoring and logging',
                ],
                iconBg: 'bg-teal-100 text-teal-600',
                borderHover: 'hover:border-teal-200',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                ),
              },
              {
                title: 'Cybersecurity',
                description:
                  'Build defensive security skills with courses on security tools, threat analysis, and incident response—backed by exams and practical assessments.',
                items: [
                  'Network security',
                  'Penetration testing',
                  'Security monitoring',
                  'Incident response',
                ],
                iconBg: 'bg-amber-100 text-amber-600',
                borderHover: 'hover:border-amber-200',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                ),
              },
              {
                title: 'Cloud Computing',
                description:
                  'Learn to design, deploy, and manage scalable applications across major cloud platforms with guided lessons and progress tracking.',
                items: [
                  'AWS/Azure/GCP services',
                  'Serverless architecture',
                  'Cloud security',
                  'Cost optimization',
                ],
                iconBg: 'bg-violet-100 text-violet-600',
                borderHover: 'hover:border-violet-200',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                ),
              },
            ].map((path) => (
              <div
                key={path.title}
                className={`group rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${path.borderHover}`}
              >
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${path.iconBg}`}>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {path.icon}
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{path.title}</h3>
                <p className="mt-2 text-base text-slate-600 leading-relaxed">{path.description}</p>
                <ul className="mt-4 space-y-1.5 text-sm text-slate-500">
                  {path.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-amber-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-amber-700">Why Choose Us</h2>
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Benefits of Phino LMS
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: 'Structured Courses',
                description:
                  'Enroll in well-organized courses with clear learning outcomes and content you can follow at your pace.',
                iconBg: 'bg-blue-100 text-blue-600',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                ),
              },
              {
                title: 'Progress Tracking',
                description:
                  'Monitor completion, stay on track with your enrollments, and see how far you have come in every course.',
                iconBg: 'bg-teal-100 text-teal-600',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                ),
              },
              {
                title: 'Assessments & Exams',
                description:
                  'Validate what you have learned with quizzes and exams designed by tutors for each course.',
                iconBg: 'bg-amber-100 text-amber-600',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                ),
              },
              {
                title: 'Certificates & Badges',
                description:
                  'Earn certificates and badges that recognize your achievements as you complete courses and milestones.',
                iconBg: 'bg-orange-100 text-orange-600',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                ),
              },
              {
                title: 'Tutor Mentorship',
                description:
                  'Learn from experienced tutors who create courses, guide enrollments, and support your development.',
                iconBg: 'bg-violet-100 text-violet-600',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                ),
              },
              {
                title: 'Flexible Learning',
                description:
                  'Access courses when it suits you—learn remotely, track your own pace, and fit study around your schedule.',
                iconBg: 'bg-rose-100 text-rose-600',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                ),
              },
            ].map((benefit) => (
              <div
                key={benefit.title}
                className="rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-xl ${benefit.iconBg}`}>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {benefit.icon}
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-base text-slate-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Login/Register Section */}
      <section id="login-section" className="py-16 sm:py-20 bg-gradient-to-b from-orange-50/50 to-amber-50/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-amber-700">Get Started</h2>
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Join Phino LMS
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-lg sm:text-xl text-slate-600">
              Ready to start learning? Continue with your organization&apos;s SSO to enroll in courses and track your progress.
            </p>
          </div>

          <div className="mt-10 max-w-md mx-auto rounded-2xl border border-amber-100 bg-white p-6 sm:p-8 shadow-xl shadow-amber-900/5">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Already have an account?</h3>
                <SSOLogin 
                  onSuccess={() => navigate('/dashboard')}
                  onError={(error) => console.error('Login error:', error)}
                />
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-slate-500">Or</span>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">New to our platform?</h3>
                <SSORegister 
                  onSuccess={() => navigate('/dashboard')}
                  onError={(error) => console.error('Registration error:', error)}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-amber-700">Contact Us</h2>
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Get in Touch
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-lg sm:text-xl text-slate-600">
              Have questions about Phino LMS or our courses? We&apos;d love to hear from you.
            </p>
          </div>

          <div className="mt-10 max-w-lg mx-auto grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-100 bg-amber-50/40 p-6 text-center shadow-sm">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">Email</h3>
              <p className="mt-2 text-base">
                <a href="mailto:lms@phinosoft.co.ke" className="text-blue-600 hover:text-blue-500 font-medium">
                  lms@phinosoft.co.ke
                </a>
              </p>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-teal-50/40 p-6 text-center shadow-sm">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-teal-100 text-teal-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">Phone</h3>
              <p className="mt-2 text-base">
                <a href="tel:+254700000000" className="text-blue-600 hover:text-blue-500 font-medium">
                  +254 700 000 000
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-teal-500 text-white">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">Phino LMS</h3>
            </div>
            <p className="mt-2 text-slate-400">
              Learning management for students and tutors
            </p>
            <div className="mt-8 flex justify-center space-x-6">
              <a href="#" className="text-slate-400 hover:text-amber-300 transition-colors">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-amber-300 transition-colors">
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            </div>
            <div className="mt-8 border-t border-slate-700 pt-8">
              <p className="text-slate-500 text-sm">
                © 2024 PhinoSoft. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
